import {create} from 'zustand';
import {User} from "/types/user-type";
import {persist} from "zustand/middleware";
import {ROLES} from "../../../constant/roles";

type ScopedRoles = Record<string, string[]>;
type UserRoles = string[] | ScopedRoles;
type UserWithScopedRoles = Omit<User, 'roles'> & {
  roles?: UserRoles;
};

export interface CurrentUser {
  id: string;
  email: string;
  userName?: string;
  firstName: string;
  lastName: string;
  profileImageUrl?: string;
  phoneNumber?: string | null;
  country?: string;
  organizationId?: string;
  roles?: string[];
  scopedRoles?: ScopedRoles;
  permissions?: string[];
  active?: boolean;
  lastLoggedInTime?: string;
}

interface CommonStoreState {
  user: CurrentUser | null;
  isAdmin: boolean;
  isOrgAdmin: boolean;
  isSuperAdmin: boolean;
  isOnlyOrgUser: boolean;
  isAppUser: boolean;
  isOnlyAppUser: boolean;
  setUser: (user: UserWithScopedRoles | null) => void;
  reset: () => void;
}

const normalizeScopedRoles = (roles?: UserRoles): ScopedRoles => {
  if (!roles) {
    return {};
  }

  if (Array.isArray(roles)) {
    return { default: roles };
  }

  return roles;
};

const getRoleNames = (roles?: UserRoles): string[] => {
  const scopedRoles = normalizeScopedRoles(roles);
  const groupedRoleNames = Object.values(scopedRoles).flatMap((groupRoles) => groupRoles ?? []);

  return Array.from(new Set(groupedRoleNames));
};

export const useCommonStore = create<CommonStoreState>()(
  persist(
    (set) => ({
      user: null,
      isAdmin: false,
      isOrgAdmin: false,
      isSuperAdmin: false,
      isOnlyOrgUser: false,
      isAppUser: false,
      isOnlyAppUser: false,
      setUser: (user) => {
        if (!user) {
          set({ user: null, isAdmin: false, isOrgAdmin: false, isSuperAdmin: false });
          return;
        }

        const scopedRoles = normalizeScopedRoles(user.roles);
        const roleNames = getRoleNames(user.roles);

        set({
          user: {
            id: user.itemId,
            email: user.email,
            userName: user.userName,
            firstName: user.firstName,
            lastName: user.lastName,
            profileImageUrl: user.profileImageUrl,
            phoneNumber: user.phoneNumber,
            country: user.country,
            organizationId: user.organizationId,
            roles: roleNames,
            scopedRoles,
            permissions: user.permissions,
            active: user.active,
            lastLoggedInTime: user.lastLoggedInTime,
          },
          isAdmin: roleNames.includes(ROLES.ADMIN),
          isOrgAdmin: roleNames.includes(ROLES.ADMIN),
          isSuperAdmin: roleNames.includes('super_admin'),
          isOnlyOrgUser: roleNames.includes(ROLES.USER) && !roleNames.includes(ROLES.ADMIN),
          isAppUser: roleNames.includes(ROLES.USER),
          isOnlyAppUser: roleNames.length === 1 && roleNames.includes(ROLES.USER)
        });
      },
      reset: () => {
        set({
          user: null,
          isAdmin: false,
          isOrgAdmin: false,
          isSuperAdmin: false,
          isOnlyOrgUser: false,
          isAppUser: false,
          isOnlyAppUser: false,
        });
      },
    }),
    {name: 'user-storage'}
  )
);
